import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "../firebaseConfig";
import { useServiceTypes, withCurrentValue } from "../utils/serviceTypeOptions";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
// Priority keeps its historical file name (capital B). On macOS NoteBook.css
// and Notebook.css are the same file, so there must only ever be this one.
import "./NoteBook.css";

/* ------------------------------------------------------------------ *
 * Config
 * ------------------------------------------------------------------ */

// Firestore collection that holds every pad line.
const NOTES_COLLECTION = "notebookNotes";

/* --- Projects ------------------------------------------------------
 * Projects live in their own two collections rather than inside
 * notebookNotes. The pad listener is filtered `where ownerId == the pad
 * being viewed`; a project is shared by the whole company and has no
 * single owner, so a project stored as a note would either need that
 * filter removed (breaking every personal pad) or would vanish the
 * moment you switched pads. Separate collections keep both behaviours
 * intact and let the project listeners run unfiltered.
 * ------------------------------------------------------------------ */
const PROJECTS_COLLECTION = "notebookProjects";
const PROJECT_LINES_COLLECTION = "notebookProjectLines";

// Jobs created off a project line land here, same collection Scheduling
// writes to, so they show up on the board, calendar and completion pages
// with no extra plumbing.
const JOBS_COLLECTION = "jobs";

// Storage folder. Lives under /notes/** so your existing storage.rules
// already allow it — no rules deploy needed.
const STORAGE_ROOT = "notes/notebook";

// How many days out a due date starts glowing yellow. 0 disables the
// warning tier — a line would go straight from clear to "due today".
const DUE_SOON_DAYS = 3;

// Safety net: if your `users` docs don't carry a role field yet, put your
// own login email(s) here so you still get the admin user switcher.
const ADMIN_EMAIL_FALLBACK = [
  // "you@yourcompany.com",
];

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const displayName = (u) => {
  if (!u) return "Unknown user";
  const joined = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return (
    u.name ||
    u.fullName ||
    u.displayName ||
    (joined || null) ||
    u.email ||
    "Unnamed user"
  );
};

const isAdminUser = (u) => {
  if (!u) return false;
  if (u.isAdmin === true || u.admin === true) return true;
  const role = String(
    u.role || u.userRole || u.accessLevel || u.permission || ""
  ).toLowerCase();
  if (role.includes("admin") || role.includes("owner")) return true;
  if (u.email && ADMIN_EMAIL_FALLBACK.includes(String(u.email).toLowerCase())) {
    return true;
  }
  return false;
};

// 08/03/26 — the stamp that sits in front of every line.
const stampDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const fullDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/* --- Due dates -----------------------------------------------------
 * Stored as plain "YYYY-MM-DD", not ISO. A due date is a calendar day,
 * not an instant: "2026-08-14T00:00:00.000Z" reads as the 13th for
 * anyone west of UTC. The plain string also sorts with localeCompare,
 * the same way createdAt already does.
 * ------------------------------------------------------------------ */

const ymd = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const todayYmd = () => ymd(new Date());

// new Date("2026-08-14") parses as UTC midnight and lands on the 13th
// in every US timezone. Build it from the parts instead.
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// Whole days from today to the due day. Negative means overdue.
// Math.round absorbs the 23- and 25-hour days at DST changeover.
const daysUntilDue = (due, today) => {
  const a = parseYmd(due);
  const b = parseYmd(today);
  if (!a || !b) return null;
  return Math.round((a - b) / 86400000);
};

// "" | "soon" | "today" | "overdue".
// Completed lines always return "" — that's what drops the highlight
// the instant a line is checked off, with no extra write.
const dueStatus = (note, today) => {
  if (!note || note.completed || !note.dueDate) return "";
  const n = daysUntilDue(note.dueDate, today);
  if (n === null) return "";
  if (n < 0) return "overdue";
  if (n === 0) return "today";
  if (n <= DUE_SOON_DAYS) return "soon";
  return "";
};

// Text on the little chip that sits after the line.
const dueChip = (note, today) => {
  if (!note.dueDate) return "";
  const short = `${note.dueDate.slice(5, 7)}/${note.dueDate.slice(8, 10)}`;
  if (note.completed) return `Due ${short}`;
  const n = daysUntilDue(note.dueDate, today);
  if (n === null) return `Due ${short}`;
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} late`;
  if (n === 0) return "Due today";
  if (n === 1) return "Due tomorrow";
  return `Due ${short}`;
};

// Long form of a due day. Deliberately separate from fullDate(), which
// appends a clock time — a due date has no time of day to show.
const dueLong = (s) => {
  const d = parseYmd(s);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Splits note text so search words can be wrapped in <mark>.
const highlight = (text, terms) => {
  const value = text || "";
  if (!terms.length) return value;
  const alt = terms.map(escapeRegExp).join("|");
  const splitter = new RegExp(`(${alt})`, "gi");
  // Separate, non-global, anchored matcher. Testing with the /g regex
  // would advance lastIndex between calls and mis-mark every other part.
  const isHit = new RegExp(`^(?:${alt})$`, "i");
  return value.split(splitter).map((part, i) =>
    part && isHit.test(part) ? (
      <mark key={i} className="np-mark">
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const matchesSearch = (note, terms) => {
  if (!terms.length) return true;
  const haystack = [
    note.text,
    note.createdByName,
    ...(note.files || []).map((f) => f.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

// A project line carries more searchable fields than a pad line does.
const matchesLineSearch = (line, terms) => {
  if (!terms.length) return true;
  const haystack = [
    line.text,
    line.serviceType,
    line.jobWo,
    line.createdByName,
    ...(line.techAssign || []),
    ...(line.files || []).map((f) => f.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

const matchesProjectSearch = (project, terms) => {
  if (!terms.length) return true;
  const haystack = [
    project.name,
    project.siteName,
    project.management,
    project.scope,
    project.woStem,
    project.createdByName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

/* --- Work order stems ----------------------------------------------
 * Lifted verbatim from Scheduling.js so a WO created here is numbered
 * by exactly the same rules as one created there. If the numbering ever
 * changes it has to change in both places, but duplicating five small
 * pure functions is far safer than importing them out of a 4,400 line
 * page component that would drag its whole module graph along with it.
 *
 * 5456 is the stem. Its branches are 5456-2, 5456-3, and so on. The
 * stem itself counts as branch 1, which is why 1 is pre-seeded into the
 * used set below — the first job made off a project is always -2.
 * ------------------------------------------------------------------ */

const normalizeWOValue = (value = "") => String(value || "").trim();

const getWOStemRoot = (wo = "") => {
  const text = normalizeWOValue(wo);
  if (!text) return "";

  // 3345-2 -> 3345
  const numericBranch = text.match(/^(\d+)-\d+$/);
  if (numericBranch) return numericBranch[1];

  // ABC-10021-2 -> ABC-10021, but ABC-10021 stays ABC-10021
  const prefixedBranch = text.match(/^([A-Za-z]+-\d+)-\d+$/);
  if (prefixedBranch) return prefixedBranch[1];

  return text;
};

const getWOStemBranchNumber = (wo = "", stem = "") => {
  const text = normalizeWOValue(wo);
  const root = getWOStemRoot(stem || text);

  if (!text || !root) return null;
  if (text === root) return 1;

  const match = text.match(
    new RegExp(`^${root.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-(\\d+)$`)
  );
  return match ? Number(match[1]) : null;
};

const getNextAvailableStemWOFromList = (jobsList = [], selectedStem = "") => {
  const root = getWOStemRoot(selectedStem);
  if (!root) return "";

  const usedBranchNumbers = new Set([1]);

  jobsList.forEach((item) => {
    const branchNumber = getWOStemBranchNumber(item?.wo, root);
    if (Number.isFinite(branchNumber)) usedBranchNumbers.add(branchNumber);
  });

  let nextBranch = 2;
  while (usedBranchNumbers.has(nextBranch)) {
    nextBranch += 1;
  }

  return `${root}-${nextBranch}`;
};

// Reads jobs fresh every time on purpose. Two people making work orders
// off the same project within a second of each other would otherwise
// both be handed the same branch number from a stale cache.
const getNextAvailableStemWO = async (selectedStem = "") => {
  const root = getWOStemRoot(selectedStem);
  if (!root) return "";

  const snap = await getDocs(collection(db, JOBS_COLLECTION));
  const allJobs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  return getNextAvailableStemWOFromList(allJobs, root);
};

// Groups the jobs list into pickable "WO — scope" entries for one site.
const buildWOOptionsForSite = (jobsList = [], siteName = "") => {
  const site = String(siteName || "").trim().toLowerCase();
  if (!site) return [];

  const map = new Map();

  jobsList.forEach((item) => {
    const itemSite = String(item?.siteName || item?.propertyName || "")
      .trim()
      .toLowerCase();
    if (itemSite !== site) return;
    if (item?.status === "Deleted") return;

    const root = getWOStemRoot(item?.wo);
    if (!root) return;

    const existing = map.get(root) || {
      root,
      siteName: item.siteName || item.propertyName || "",
      management: item.management || "",
      address: item.address || "",
      city: item.city || "",
      state: item.state || "",
      zip: item.zip || "",
      scope: "",
      jobId: item.id,
      count: 0,
    };

    existing.count += 1;

    // Prefer the stem job's own scope over a branch's.
    if (getWOStemBranchNumber(item?.wo, root) === 1 || !existing.scope) {
      existing.scope = item.scope || item.jobDescription || existing.scope;
      existing.management = existing.management || item.management || "";
      existing.address = existing.address || item.address || "";
      existing.city = existing.city || item.city || "";
      existing.state = existing.state || item.state || "";
      existing.zip = existing.zip || item.zip || "";
    }

    map.set(root, existing);
  });

  return Array.from(map.values()).sort((a, b) =>
    String(b.root).localeCompare(String(a.root), undefined, { numeric: true })
  );
};

/* --- Priority pricing ---------------------------------------------
 * Same rules Priority's Scheduling.js uses (readPropertyPricing /
 * resolveServicePricing): a per-property override wins, then the legacy
 * per-property service totals, then the PPD service template. A job made
 * off a notebook line therefore carries the same initial-fee / recurring
 * fee numbers it would get if it had been scheduled by hand.
 * ------------------------------------------------------------------ */

const SERVICE_TEMPLATES_COLLECTION = "proposalsPPDServiceTypes";

const getServiceCode = (value = "") =>
  String(value || "").trim().split(/\s+-\s+|\s+/)[0].toUpperCase();

const readPropertyPricing = (property = {}, serviceType = "") => {
  const code = getServiceCode(serviceType);
  const overrides = property?.servicePricingOverrides || {};
  const override = overrides[code] || overrides[serviceType] || {};
  const legacy = property?.serviceTypeTotals || property?.serviceTotals || {};
  return {
    amount:
      override.amount ?? override.total ?? legacy[code] ?? legacy[serviceType] ?? "",
    initialAmount: override.initialAmount ?? "",
    feeSchedule: override.feeSchedule || "",
  };
};

const resolveServicePricing = (property = {}, template = {}, serviceType = "") => {
  const override = readPropertyPricing(property, serviceType);
  return {
    amount:
      override.amount !== "" && override.amount != null
        ? override.amount
        : template?.total ?? "",
    initialAmount:
      override.initialAmount !== "" && override.initialAmount != null
        ? override.initialAmount
        : template?.initialAmount ?? "",
    feeSchedule: override.feeSchedule || template?.feeSchedule || "Monthly",
  };
};

// Reads the property + PPD template fresh when a work order is made, so
// pricing edited elsewhere since the pad was opened is honoured.
const loadPricingFor = async (siteName = "", serviceType = "") => {
  try {
    const [propSnap, tplSnap] = await Promise.all([
      getDocs(query(collection(db, "properties"), where("propertyName", "==", siteName))),
      getDocs(collection(db, SERVICE_TEMPLATES_COLLECTION)),
    ]);
    const property = propSnap.docs[0]?.data() || {};
    const template =
      tplSnap.docs.map((d) => d.data()).find((t) => t.serviceType === serviceType) || {};
    return resolveServicePricing(property, template, serviceType);
  } catch (e) {
    console.error("Notebook: could not load pricing", e);
    return { amount: "", initialAmount: "", feeSchedule: "Monthly" };
  }
};

/* --- Popups --------------------------------------------------------
 * Several Priority stylesheets define .modal / .modal-overlay with
 * conflicting !important rules, so every Notebook popup is portalled to
 * <body> with self-contained inline positioning. The np-modal-scope
 * class re-declares the pad's CSS variables (see NoteBook.css) because a
 * portalled node no longer sits inside .np-page.
 * ------------------------------------------------------------------ */

const NpModal = ({ onClose, wide = false, labelledBy, children }) =>
  createPortal(
    <div
      className="np-modal-scope"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 999998,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        className={`np-dialog${wide ? " np-dialog--wide" : ""}`}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)",
          maxWidth: wide ? 620 : 420,
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          boxSizing: "border-box",
          background: "#ffffff",
          padding: 24,
          borderRadius: 12,
          boxShadow: "0 20px 50px -12px rgba(23, 36, 46, 0.45)",
          zIndex: 999999,
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Notebook = () => {
  const storage = getStorage();

  const [authUser, setAuthUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [users, setUsers] = useState([]);
  const [viewUserId, setViewUserId] = useState(null);

  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);

  const [tab, setTab] = useState("open"); // "open" | "completed" | "projects"
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("oldest"); // "oldest" | "newest"

  /* --- Projects ---------------------------------------------------- */

  const [projects, setProjects] = useState([]);
  const [projectLines, setProjectLines] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [activeProjectId, setActiveProjectId] = useState(null);

  // Loaded lazily — only the New Project modal needs them, and the jobs
  // collection is far too big to pull on every visit to the pad.
  const [properties, setProperties] = useState([]);
  const [jobsForPicker, setJobsForPicker] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);

  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [npSite, setNpSite] = useState("");
  const [npWoStem, setNpWoStem] = useState("");
  const [npName, setNpName] = useState("");

  // Project line draft.
  const [lineDraftOpen, setLineDraftOpen] = useState(false);
  const [lineText, setLineText] = useState("");
  const [lineFiles, setLineFiles] = useState([]);
  const [lineDue, setLineDue] = useState("");
  const [lineServiceType, setLineServiceType] = useState("");
  const [lineTechs, setLineTechs] = useState([""]);
  const [lineDraftKey] = useState(() => `pl${Date.now()}`);

  const [editingLineId, setEditingLineId] = useState(null);
  const [lineEdit, setLineEdit] = useState(null); // full draft of the line

  const [lineDueTarget, setLineDueTarget] = useState(null);
  const [confirmingLine, setConfirmingLine] = useState(null); // {kind, line}
  const [confirmingProject, setConfirmingProject] = useState(null);

  // Work order creation.
  const [woTarget, setWoTarget] = useState(null); // {mode:"one"|"all", line?}
  const [woBusy, setWoBusy] = useState(false);
  const [woResult, setWoResult] = useState("");

  const { serviceTypes } = useServiceTypes();
  const techOptions = useMemo(() => filterStaffForDropdowns(users), [users]);

  const functions = useMemo(() => getFunctions(app, "us-central1"), []);

  const [draftOpen, setDraftOpen] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftFiles, setDraftFiles] = useState([]);
  const [draftDue, setDraftDue] = useState("");
  const [draftKey] = useState(() => `d${Date.now()}`);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const [dueTarget, setDueTarget] = useState(null); // note being dated
  const [dueDraft, setDueDraft] = useState("");

  // Today's local calendar day, kept fresh so a pad left open overnight
  // doesn't keep colouring rows against yesterday.
  const [today, setToday] = useState(todayYmd);

  const [confirming, setConfirming] = useState(null); // {kind, note}
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const draftRef = useRef(null);
  const editRef = useRef(null);
  const padEndRef = useRef(null);
  const dueInputRef = useRef(null);

  /* -------------------------------------------------- auth */

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setAuthUser(u || null);
      setAuthReady(true);
      if (u) setViewUserId((prev) => prev || u.uid);
    });
    return unsub;
  }, []);

  /* -------------------------------------------------- users */

  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.status !== "Terminated" && u.active !== false);
        list.sort((a, b) => displayName(a).localeCompare(displayName(b)));
        setUsers(list);
      } catch (e) {
        console.error("Notebook: could not load users", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authUser]);

  const myProfile = useMemo(() => {
    if (!authUser) return null;
    return (
      users.find((u) => u.id === authUser.uid) ||
      users.find(
        (u) =>
          u.email &&
          authUser.email &&
          String(u.email).toLowerCase() === String(authUser.email).toLowerCase()
      ) ||
      null
    );
  }, [users, authUser]);

  const isAdmin = useMemo(() => {
    if (isAdminUser(myProfile)) return true;
    if (
      authUser?.email &&
      ADMIN_EMAIL_FALLBACK.includes(String(authUser.email).toLowerCase())
    ) {
      return true;
    }
    return false;
  }, [myProfile, authUser]);

  const viewingSelf = !!authUser && viewUserId === authUser.uid;

  const viewUser = useMemo(() => {
    if (viewingSelf) {
      return myProfile || { id: authUser?.uid, email: authUser?.email };
    }
    return users.find((u) => u.id === viewUserId) || null;
  }, [viewingSelf, myProfile, authUser, users, viewUserId]);

  const viewUserName = viewingSelf
    ? displayName(myProfile || { email: authUser?.email })
    : displayName(viewUser);

  /* -------------------------------------------------- notes (live) */

  useEffect(() => {
    if (!viewUserId) return;
    setLoadingNotes(true);

    const q = query(
      collection(db, NOTES_COLLECTION),
      where("ownerId", "==", viewUserId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingNotes(false);
      },
      (e) => {
        console.error("Notebook: notes listener failed", e);
        setError("Could not load this pad. Check your connection and reload.");
        setLoadingNotes(false);
      }
    );

    return unsub;
  }, [viewUserId]);

  /* -------------------------------------------------- projects (live) */

  // Deliberately unfiltered. Projects belong to the company, not to a
  // pad, so every signed-in user sees the same list no matter whose pad
  // is on screen.
  useEffect(() => {
    if (!authUser) return;

    const unsubProjects = onSnapshot(
      collection(db, PROJECTS_COLLECTION),
      (snap) => {
        setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingProjects(false);
      },
      (e) => {
        console.error("Notebook: projects listener failed", e);
        setError("Could not load projects. Check your connection and reload.");
        setLoadingProjects(false);
      }
    );

    const unsubLines = onSnapshot(
      collection(db, PROJECT_LINES_COLLECTION),
      (snap) => {
        setProjectLines(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (e) => {
        console.error("Notebook: project lines listener failed", e);
      }
    );

    return () => {
      unsubProjects();
      unsubLines();
    };
  }, [authUser]);

  // Properties and jobs only load the first time something needs them,
  // which is the New Project modal. Nothing else on the pad touches them.
  const ensurePickerData = useCallback(async () => {
    if (pickerLoaded || pickerLoading) return;
    setPickerLoading(true);
    try {
      const [propSnap, jobSnap] = await Promise.all([
        getDocs(collection(db, "properties")),
        getDocs(collection(db, JOBS_COLLECTION)),
      ]);

      const propList = propSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.propertyName)
        .sort((a, b) =>
          String(a.propertyName).localeCompare(String(b.propertyName))
        );

      setProperties(propList);
      setJobsForPicker(jobSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPickerLoaded(true);
    } catch (e) {
      console.error("Notebook: could not load sites and work orders", e);
      setError("Could not load sites and work orders. Try again.");
    } finally {
      setPickerLoading(false);
    }
  }, [pickerLoaded, pickerLoading]);

  // Switching pads should never carry a half-typed line across.
  useEffect(() => {
    setDraftOpen(false);
    setDraftText("");
    setDraftFiles([]);
    setDraftDue("");
    setEditingId(null);
    setEditText("");
    setDueTarget(null);
    setDueDraft("");
  }, [viewUserId]);

  // Poll for the day rolling over. setState with an identical string is
  // a no-op in React, so this only causes a re-render at midnight.
  useEffect(() => {
    const id = setInterval(() => setToday(todayYmd()), 60000);
    return () => clearInterval(id);
  }, []);

  /* -------------------------------------------------- derived lists */

  const terms = useMemo(
    () =>
      search
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean),
    [search]
  );

  const openNotes = useMemo(
    () => notes.filter((n) => !n.completed),
    [notes]
  );
  const doneNotes = useMemo(
    () => notes.filter((n) => n.completed),
    [notes]
  );

  const visible = useMemo(() => {
    const base = tab === "open" ? openNotes : doneNotes;
    const key = tab === "open" ? "createdAt" : "completedAt";
    const filtered = base.filter((n) => matchesSearch(n, terms));
    return filtered.sort((a, b) => {
      const av = a[key] || a.createdAt || "";
      const bv = b[key] || b.createdAt || "";
      return sortDir === "oldest" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [tab, openNotes, doneNotes, terms, sortDir]);

  // How many hits are hiding on the other tab. Projects are excluded on
  // purpose — this hint is about the Pad/Completed pair only.
  const otherTabHits = useMemo(() => {
    if (!terms.length) return 0;
    if (tab === "projects") return 0;
    const other = tab === "open" ? doneNotes : openNotes;
    return other.filter((n) => matchesSearch(n, terms)).length;
  }, [terms, tab, openNotes, doneNotes]);

  /* -------------------------------------------------- project derived */

  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId) || null,
    [projects, activeProjectId]
  );

  // Lines indexed by project so the cards can show counts without a
  // filter pass per card.
  const linesByProject = useMemo(() => {
    const map = new Map();
    projectLines.forEach((line) => {
      if (!line.projectId) return;
      const list = map.get(line.projectId) || [];
      list.push(line);
      map.set(line.projectId, list);
    });
    return map;
  }, [projectLines]);

  const activeLines = useMemo(() => {
    if (!activeProjectId) return [];
    const list = (linesByProject.get(activeProjectId) || []).filter((l) =>
      matchesLineSearch(l, terms)
    );
    return list.sort((a, b) => {
      const av = a.createdAt || "";
      const bv = b.createdAt || "";
      return sortDir === "oldest" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [activeProjectId, linesByProject, terms, sortDir]);

  const visibleProjects = useMemo(() => {
    const list = projects.filter((p) => matchesProjectSearch(p, terms));
    return list.sort((a, b) => {
      const av = a.createdAt || "";
      const bv = b.createdAt || "";
      return sortDir === "oldest" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [projects, terms, sortDir]);

  // Work order options for the site chosen in the New Project modal.
  const npWoOptions = useMemo(
    () => buildWOOptionsForSite(jobsForPicker, npSite),
    [jobsForPicker, npSite]
  );

  const npChosenWo = useMemo(
    () => npWoOptions.find((o) => o.root === npWoStem) || null,
    [npWoOptions, npWoStem]
  );

  // Lines that are ready to become work orders: not yet turned into one,
  // and carrying the two fields a job cannot be built without.
  const convertibleLines = useMemo(
    () =>
      (linesByProject.get(activeProjectId) || []).filter(
        (l) => !l.jobId && l.serviceType && (l.text || "").trim()
      ),
    [linesByProject, activeProjectId]
  );

  /* -------------------------------------------------- textarea autosize */

  const autosize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autosize(draftRef.current);
  }, [draftText, draftOpen]);

  useEffect(() => {
    autosize(editRef.current);
  }, [editText, editingId]);

  /* -------------------------------------------------- actions */

  const startDraft = useCallback(() => {
    // The draft row only lives on the Pad tab, so + New always goes there.
    setTab("open");
    setDraftOpen(true);
    setEditingId(null);
    window.requestAnimationFrame(() => {
      draftRef.current?.focus();
      padEndRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  }, []);

  const cancelDraft = () => {
    setDraftOpen(false);
    setDraftText("");
    setDraftFiles([]);
    setDraftDue("");
  };

  const uploadFiles = async (fileList, folder) => {
    const out = [];
    for (const file of Array.from(fileList)) {
      const safe = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `${STORAGE_ROOT}/${viewUserId}/${folder}/${Date.now()}_${safe}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      out.push({ name: file.name, url, path, size: file.size });
    }
    return out;
  };

  const handleDraftFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadFiles(files, draftKey);
      setDraftFiles((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error("Notebook: draft upload failed", err);
      setError("That file didn't upload. Try again, or use a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleNoteFiles = async (note, e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadFiles(files, note.id);
      await updateDoc(doc(db, NOTES_COLLECTION, note.id), {
        files: [...(note.files || []), ...uploaded],
      });
    } catch (err) {
      console.error("Notebook: attach failed", err);
      setError("That file didn't upload. Try again, or use a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveDraft = async () => {
    const text = draftText.trim();
    if (!text && draftFiles.length === 0) return;
    setBusy(true);
    setError("");
    try {
      await addDoc(collection(db, NOTES_COLLECTION), {
        ownerId: viewUserId,
        ownerName: viewUserName,
        text,
        files: draftFiles,
        dueDate: draftDue || null,
        completed: false,
        completedAt: null,
        completedBy: null,
        createdAt: new Date().toISOString(),
        createdBy: authUser.uid,
        createdByName: displayName(myProfile || { email: authUser.email }),
        updatedAt: null,
      });
      setDraftText("");
      setDraftFiles([]);
      setDraftDue("");
      setDraftOpen(false);
    } catch (err) {
      console.error("Notebook: save failed", err);
      setError("That line didn't save. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    const text = editText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, NOTES_COLLECTION, editingId), {
        text,
        updatedAt: new Date().toISOString(),
      });
      setEditingId(null);
      setEditText("");
    } catch (err) {
      console.error("Notebook: edit failed", err);
      setError("That edit didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const openDueEditor = (note) => {
    setDueTarget(note);
    setDueDraft(note.dueDate || "");
    // Nothing else should be mid-edit behind the modal.
    setEditingId(null);
    setDraftOpen(false);
    window.requestAnimationFrame(() => dueInputRef.current?.focus());
  };

  // Pass "" to clear. Writes null rather than deleting the field so the
  // shape of the doc stays consistent across old and new lines.
  const saveDue = async (value) => {
    if (!dueTarget) return;
    setBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, NOTES_COLLECTION, dueTarget.id), {
        dueDate: value || null,
      });
      setDueTarget(null);
      setDueDraft("");
    } catch (err) {
      console.error("Notebook: due date failed", err);
      setError("That due date didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Quick picks in the modal. Offsets are applied to a locally-built
  // date so month and year rollover is the engine's problem, not ours.
  const shiftToday = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return ymd(d);
  };

  /* -------------------------------------------------- project actions */

  const openNewProject = async () => {
    setTab("projects");
    setActiveProjectId(null);
    setNewProjectOpen(true);
    setNpSite("");
    setNpWoStem("");
    setNpName("");
    setError("");
    await ensurePickerData();
  };

  const closeNewProject = () => {
    setNewProjectOpen(false);
    setNpSite("");
    setNpWoStem("");
    setNpName("");
  };

  const saveNewProject = async () => {
    if (!npSite || !npWoStem) return;
    setBusy(true);
    setError("");
    try {
      const chosen = npChosenWo;
      const property = properties.find((p) => p.propertyName === npSite) || {};

      const ref = await addDoc(collection(db, PROJECTS_COLLECTION), {
        name: npName.trim(),
        siteName: npSite,
        // Property record wins over the job's copy — it is the record
        // that gets maintained, so it is the one that is current.
        management: property.management || chosen?.management || "",
        address: property.address || chosen?.address || "",
        city: property.city || chosen?.city || "",
        state: property.state || chosen?.state || "",
        zip: property.zip || chosen?.zip || "",
        woStem: npWoStem,
        scope: chosen?.scope || "",
        anchorJobId: chosen?.jobId || "",
        status: "Active",
        createdAt: new Date().toISOString(),
        createdBy: authUser.uid,
        createdByName: displayName(myProfile || { email: authUser.email }),
      });

      setActiveProjectId(ref.id);
      closeNewProject();
    } catch (err) {
      console.error("Notebook: project save failed", err);
      setError("That project didn't save. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const resetLineDraft = () => {
    setLineDraftOpen(false);
    setLineText("");
    setLineFiles([]);
    setLineDue("");
    setLineServiceType("");
    setLineTechs([""]);
  };

  const startLineDraft = () => {
    setTab("projects");
    setEditingLineId(null);
    setLineDraftOpen(true);
    window.requestAnimationFrame(() => {
      padEndRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  };

  const handleLineDraftFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadFiles(files, `projects/${lineDraftKey}`);
      setLineFiles((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error("Notebook: project line upload failed", err);
      setError("That file didn't upload. Try again, or use a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleLineFiles = async (line, e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadFiles(files, `projects/${line.id}`);
      await updateDoc(doc(db, PROJECT_LINES_COLLECTION, line.id), {
        files: [...(line.files || []), ...uploaded],
      });
    } catch (err) {
      console.error("Notebook: project line attach failed", err);
      setError("That file didn't upload. Try again, or use a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const cleanTechs = (list) =>
    Array.from(new Set((list || []).map((t) => String(t || "").trim()))).filter(
      Boolean
    );

  const saveLineDraft = async () => {
    if (!activeProjectId) return;
    const text = lineText.trim();
    if (!text && !lineFiles.length) return;

    setBusy(true);
    setError("");
    try {
      await addDoc(collection(db, PROJECT_LINES_COLLECTION), {
        projectId: activeProjectId,
        text,
        files: lineFiles,
        dueDate: lineDue || null,
        serviceType: lineServiceType || "",
        techAssign: cleanTechs(lineTechs),
        completed: false,
        completedAt: null,
        completedBy: null,
        jobId: "",
        jobWo: "",
        jobCreatedAt: null,
        createdAt: new Date().toISOString(),
        createdBy: authUser.uid,
        createdByName: displayName(myProfile || { email: authUser.email }),
        updatedAt: null,
      });
      resetLineDraft();
    } catch (err) {
      console.error("Notebook: project line save failed", err);
      setError("That line didn't save. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const startLineEdit = (line) => {
    setEditingLineId(line.id);
    setLineDraftOpen(false);
    setLineEdit({
      text: line.text || "",
      dueDate: line.dueDate || "",
      serviceType: line.serviceType || "",
      techAssign: (line.techAssign || []).length ? [...line.techAssign] : [""],
    });
  };

  const saveLineEdit = async () => {
    if (!editingLineId || !lineEdit) return;
    setBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, PROJECT_LINES_COLLECTION, editingLineId), {
        text: lineEdit.text.trim(),
        dueDate: lineEdit.dueDate || null,
        serviceType: lineEdit.serviceType || "",
        techAssign: cleanTechs(lineEdit.techAssign),
        updatedAt: new Date().toISOString(),
      });
      setEditingLineId(null);
      setLineEdit(null);
    } catch (err) {
      console.error("Notebook: project line edit failed", err);
      setError("That edit didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const saveLineDue = async (value) => {
    if (!lineDueTarget) return;
    setBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, PROJECT_LINES_COLLECTION, lineDueTarget.id), {
        dueDate: value || null,
      });
      setLineDueTarget(null);
      setDueDraft("");
    } catch (err) {
      console.error("Notebook: project line due date failed", err);
      setError("That due date didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const runLineConfirm = async () => {
    if (!confirmingLine) return;
    const { kind, line } = confirmingLine;
    setBusy(true);
    setError("");
    try {
      if (kind === "complete") {
        await updateDoc(doc(db, PROJECT_LINES_COLLECTION, line.id), {
          completed: true,
          completedAt: new Date().toISOString(),
          completedBy: displayName(myProfile || { email: authUser.email }),
        });
      } else if (kind === "reopen") {
        await updateDoc(doc(db, PROJECT_LINES_COLLECTION, line.id), {
          completed: false,
          completedAt: null,
          completedBy: null,
        });
      } else if (kind === "delete") {
        await deleteDoc(doc(db, PROJECT_LINES_COLLECTION, line.id));
      }
      setConfirmingLine(null);
    } catch (err) {
      console.error("Notebook: project line action failed", err);
      setError("That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Deleting a project removes its lines too. Any work orders already
  // made from those lines are left alone — they are real scheduled jobs
  // and are not the notebook's to delete.
  const deleteProject = async () => {
    if (!confirmingProject) return;
    setBusy(true);
    setError("");
    try {
      const lines = linesByProject.get(confirmingProject.id) || [];
      await Promise.all(
        lines.map((l) => deleteDoc(doc(db, PROJECT_LINES_COLLECTION, l.id)))
      );
      await deleteDoc(doc(db, PROJECTS_COLLECTION, confirmingProject.id));
      if (activeProjectId === confirmingProject.id) setActiveProjectId(null);
      setConfirmingProject(null);
    } catch (err) {
      console.error("Notebook: project delete failed", err);
      setError("That project didn't delete. Try again.");
    } finally {
      setBusy(false);
    }
  };

  /* -------------------------------------------------- work orders */

  // Builds one job off one line. The caller is responsible for doing
  // these strictly one at a time — see makeWorkOrders below.
  const createJobFromLine = async (project, line) => {
    const assignedWO = await getNextAvailableStemWO(project.woStem);
    const pricing = line.serviceType
      ? await loadPricingFor(project.siteName, line.serviceType)
      : { amount: "", initialAmount: "", feeSchedule: "Monthly" };

    const jobData = {
      siteName: project.siteName || "",
      management: project.management || "",
      address: project.address || "",
      city: project.city || "",
      state: project.state || "",
      zip: project.zip || "",
      email: "",
      phone: "",

      // The line's due date is when the work has to happen, so it lands
      // on the job as the start date.
      startDate: line.dueDate || "",
      startTime: "",
      endDate: "",
      endTime: "",

      wo: assignedWO,
      serviceType: line.serviceType || "",
      // Priority pest-control pricing (initial fee vs recurring fee), the
      // same fields Scheduling fills from the property / PPD template.
      estimatedTotal: pricing.amount,
      initialAmount: pricing.initialAmount,
      feeSchedule: pricing.feeSchedule,
      recurringInvoiceAmount: pricing.amount,
      recurringFeeSchedule: pricing.feeSchedule,
      scope: project.scope || "",
      jobDescription: line.text || "",
      techAssign: cleanTechs(line.techAssign),
      status: "Active",
      referral: "",
      companyUse: "",
      clientWorkOrder: "",

      // Same three fields Scheduling writes when you extend a stem, so
      // this job groups with the rest of the 5456 family everywhere.
      parentWOStem: project.woStem || "",
      extendedFromWOStem: project.woStem || "",
      isWOStemExtension: true,

      isContractJob: false,
      contractBillingFrequency: "Monthly",
      contractMonthlyAmount: "",
      contractInitialAmount: "",
      contractStartDate: "",
      contractEndDate: "",
      isProject: false,
      projectStatus: "",
      projectRootId: "",
      projectDayNumber: 1,
      projectCompletedAt: "",
      projectCompletedBy: "",

      jobPlan: { estimatedDays: "", estimatedLabor: "", materials: [] },
      recurrence: {
        type: "none",
        interval: 1,
        daysOfWeek: [],
        customDates: [],
        timesPerWeek: null,
        endsOn: "",
      },

      // Trail back to where this came from.
      sourceNotebookProjectId: project.id,
      sourceNotebookLineId: line.id,

      creationDate: new Date(),
      createdBy: {
        uid: authUser.uid,
        firstName: myProfile?.firstName || "",
        lastName: myProfile?.lastName || "",
      },
    };

    // Files were already uploaded to storage when they were attached to
    // the line, so the job reuses those URLs rather than re-uploading.
    const files = line.files || [];
    if (files.length) {
      jobData.fileURLs = files.map((f) => f.url).filter(Boolean);
      jobData.fileNames = files.map((f) => f.name).filter(Boolean);
    }

    const jobRef = await addDoc(collection(db, JOBS_COLLECTION), jobData);

    await updateDoc(doc(db, PROJECT_LINES_COLLECTION, line.id), {
      jobId: jobRef.id,
      jobWo: assignedWO,
      jobCreatedAt: new Date().toISOString(),
      jobCreatedBy: displayName(myProfile || { email: authUser.email }),
    });

    // A failed tech email must not lose the job that was just written.
    let emailed = true;
    try {
      await httpsCallable(functions, "sendScheduledJobEmail")({
        jobId: jobRef.id,
      });
    } catch (emailErr) {
      console.error("Notebook: job saved, tech email failed", emailErr);
      emailed = false;
    }

    return { wo: assignedWO, emailed };
  };

  const makeWorkOrders = async () => {
    if (!woTarget || !activeProject) return;

    const queue =
      woTarget.mode === "all" ? convertibleLines : [woTarget.line].filter(Boolean);

    if (!queue.length) return;

    setWoBusy(true);
    setError("");

    const made = [];
    let emailFailures = 0;

    try {
      // Strictly sequential. getNextAvailableStemWO re-reads the jobs
      // collection each pass, so running these in parallel would hand
      // every one of them the same branch number.
      for (const line of queue) {
        const result = await createJobFromLine(activeProject, line);
        made.push(result.wo);
        if (!result.emailed) emailFailures += 1;
      }

      let message = `Created ${made.length} work order${
        made.length === 1 ? "" : "s"
      }: ${made.join(", ")}.`;

      if (emailFailures) {
        message += ` ${emailFailures} tech email${
          emailFailures === 1 ? "" : "s"
        } did not send — check that the assigned techs have an email on file in Staff.`;
      }

      setWoResult(message);
      setWoTarget(null);
    } catch (err) {
      console.error("Notebook: work order creation failed", err);
      setError(
        made.length
          ? `Created ${made.join(", ")}, then hit an error. Nothing after that was made.`
          : "That work order didn't get created. Try again."
      );
      setWoTarget(null);
    } finally {
      setWoBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirming) return;
    const { kind, note } = confirming;
    setBusy(true);
    setError("");
    try {
      if (kind === "complete") {
        await updateDoc(doc(db, NOTES_COLLECTION, note.id), {
          completed: true,
          completedAt: new Date().toISOString(),
          completedBy: displayName(myProfile || { email: authUser.email }),
        });
      } else if (kind === "reopen") {
        await updateDoc(doc(db, NOTES_COLLECTION, note.id), {
          completed: false,
          completedAt: null,
          completedBy: null,
        });
      } else if (kind === "delete") {
        await deleteDoc(doc(db, NOTES_COLLECTION, note.id));
      }
      setConfirming(null);
    } catch (err) {
      console.error("Notebook: action failed", err);
      setError("That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Esc closes the confirm box.
  useEffect(() => {
    if (!confirming) return;
    const onKey = (e) => {
      if (e.key === "Escape") setConfirming(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirming]);

  // Esc closes the due date box.
  useEffect(() => {
    if (!dueTarget) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDueTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dueTarget]);

  // Esc closes the project dialogs. The work order dialog is skipped
  // while it is mid-run — closing it wouldn't stop the writes already in
  // flight, it would just hide them.
  useEffect(() => {
    const anyOpen =
      newProjectOpen ||
      lineDueTarget ||
      confirmingLine ||
      confirmingProject ||
      woTarget ||
      woResult;
    if (!anyOpen) return;

    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (woBusy) return;
      setNewProjectOpen(false);
      setLineDueTarget(null);
      setConfirmingLine(null);
      setConfirmingProject(null);
      setWoTarget(null);
      setWoResult("");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    newProjectOpen,
    lineDueTarget,
    confirmingLine,
    confirmingProject,
    woTarget,
    woResult,
    woBusy,
  ]);

  /* -------------------------------------------------- render guards */

  if (!authReady) {
    return (
      <div className="np-page">
        <p className="np-status">Loading your pad…</p>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="np-page">
        <p className="np-status">Sign in to open your pad.</p>
      </div>
    );
  }

  /* -------------------------------------------------- row renderer */

  const renderRow = (note) => {
    const editing = editingId === note.id;
    const borrowed = note.createdBy && note.createdBy !== note.ownerId;
    const status = dueStatus(note, today); // "" once completed

    return (
      <div
        className={`np-row${note.completed ? " np-row--done" : ""}${
          status ? ` np-row--due-${status}` : ""
        }`}
        key={note.id}
      >
        <button
          type="button"
          className="np-check"
          aria-label={
            note.completed ? "Reopen this line" : "Mark this line completed"
          }
          onClick={() =>
            setConfirming({
              kind: note.completed ? "reopen" : "complete",
              note,
            })
          }
        >
          {note.completed && (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M2.5 8.5l3.5 3.5 7.5-8.5"
                fill="none"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        <div className="np-body">
          {editing ? (
            <div className="np-edit">
              <textarea
                ref={editRef}
                className="np-input"
                value={editText}
                rows={1}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit();
                  if (e.key === "Escape") setEditingId(null);
                }}
              />
              <div className="np-editbar">
                <button
                  type="button"
                  className="np-btn np-btn--primary np-btn--sm"
                  onClick={saveEdit}
                  disabled={busy || !editText.trim()}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="np-btn np-btn--ghost np-btn--sm"
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="np-line">
              <span
                className="np-stamp"
                title={
                  note.completed
                    ? `Written ${fullDate(note.createdAt)} · Completed ${fullDate(
                        note.completedAt
                      )}`
                    : `Written ${fullDate(note.createdAt)}`
                }
              >
                {stampDate(tab === "completed" ? note.completedAt : note.createdAt)}
              </span>
              <span className="np-text">{highlight(note.text, terms)}</span>
              {borrowed && (
                <span className="np-tag" title={`Added by ${note.createdByName}`}>
                  {note.createdByName}
                </span>
              )}
              {note.updatedAt && <span className="np-tag np-tag--quiet">edited</span>}
              <button
                type="button"
                className={`np-due ${
                  note.dueDate ? `np-due--${status || "set"}` : "np-due--empty"
                }`}
                onClick={() => openDueEditor(note)}
                title={
                  note.dueDate
                    ? `Due ${note.dueDate} — click to change`
                    : "Set a due date"
                }
              >
                {note.dueDate ? dueChip(note, today) : "+ Due date"}
              </button>
            </p>
          )}

          {!!(note.files || []).length && (
            <div className="np-files">
              {note.files.map((f) => (
                <a
                  key={f.path || f.url}
                  className="np-file"
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" className="np-clip">
                    <path
                      d="M10.5 5.5L6 10a1.8 1.8 0 002.5 2.5l4.5-4.5a3.4 3.4 0 00-4.8-4.8L3.5 7.9a5 5 0 007 7l4-4"
                      fill="none"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {f.name}
                </a>
              ))}
            </div>
          )}
        </div>

        {!editing && (
          <div className="np-tools">
            <label className="np-tool" title="Attach a file">
              <input type="file" multiple onChange={(e) => handleNoteFiles(note, e)} />
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M10.5 5.5L6 10a1.8 1.8 0 002.5 2.5l4.5-4.5a3.4 3.4 0 00-4.8-4.8L3.5 7.9a5 5 0 007 7l4-4"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </label>
            <button
              type="button"
              className="np-tool"
              title="Edit this line"
              onClick={() => {
                setEditingId(note.id);
                setEditText(note.text || "");
                setDraftOpen(false);
              }}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M11.2 2.3l2.5 2.5L5.4 13 2 14l1-3.4 8.2-8.3z"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="np-tool np-tool--danger"
              title="Delete this line"
              onClick={() => setConfirming({ kind: "delete", note })}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  /* -------------------------------------------------- project renderers */

  // A tech picker that mirrors Scheduling's: one select per assigned
  // person, plus a row to add another. Values are "First Last" strings,
  // which is the shape the jobs collection and the tech email both
  // expect — matching on names, not uids.
  const renderTechPicker = (list, onChange) => (
    <div className="np-techs">
      {(list.length ? list : [""]).map((tech, index) => (
        <div className="np-techrow" key={index}>
          <select
            className="np-select"
            value={tech}
            onChange={(e) => {
              const next = [...(list.length ? list : [""])];
              next[index] = e.target.value;
              onChange(next);
            }}
          >
            <option value="">Select technician</option>
            {techOptions.map((u) => (
              <option key={u.id} value={`${u.firstName} ${u.lastName}`}>
                {u.firstName} {u.lastName}
              </option>
            ))}
            {/* Keeps an assigned name selectable after that person is
                hidden from dropdowns, so an edit can't silently blank it. */}
            {tech &&
            !techOptions.some((u) => `${u.firstName} ${u.lastName}` === tech) ? (
              <option value={tech}>{tech} (hidden)</option>
            ) : null}
          </select>
          {(list.length ? list : [""]).length > 1 && (
            <button
              type="button"
              className="np-tool np-tool--danger"
              title="Remove this technician"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="np-btn np-btn--ghost np-btn--sm"
        onClick={() => onChange([...(list.length ? list : [""]), ""])}
      >
        + Add technician
      </button>
    </div>
  );

  const renderProjectCard = (project) => {
    const lines = linesByProject.get(project.id) || [];
    const done = lines.filter((l) => l.completed).length;
    const withWO = lines.filter((l) => l.jobId).length;

    return (
      <div className="np-project" key={project.id}>
        <button
          type="button"
          className="np-project__open"
          onClick={() => {
            setActiveProjectId(project.id);
            resetLineDraft();
            setEditingLineId(null);
          }}
        >
          <span className="np-project__wo">WO {project.woStem}</span>
          <span className="np-project__site">
            {highlight(project.siteName || "No site", terms)}
          </span>
          {!!project.name && (
            <span className="np-project__name">
              {highlight(project.name, terms)}
            </span>
          )}
          {!!project.scope && (
            <span className="np-project__scope">
              {highlight(project.scope, terms)}
            </span>
          )}
          <span className="np-project__meta">
            {lines.length} line{lines.length === 1 ? "" : "s"}
            {!!done && ` · ${done} done`}
            {!!withWO && ` · ${withWO} WO`}
            {project.createdByName ? ` · by ${project.createdByName}` : ""}
          </span>
        </button>

        <div className="np-tools np-tools--project">
          <button
            type="button"
            className="np-tool np-tool--danger"
            title="Delete this project"
            onClick={() => setConfirmingProject(project)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9"
                fill="none"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderProjectLine = (line) => {
    const editing = editingLineId === line.id;
    const status = dueStatus(line, today);
    const madeWO = !!line.jobId;

    return (
      <div
        className={`np-row np-row--line${line.completed ? " np-row--done" : ""}${
          status ? ` np-row--due-${status}` : ""
        }`}
        key={line.id}
      >
        <button
          type="button"
          className="np-check"
          aria-label={
            line.completed ? "Reopen this line" : "Mark this line completed"
          }
          onClick={() =>
            setConfirmingLine({
              kind: line.completed ? "reopen" : "complete",
              line,
            })
          }
        >
          {line.completed && (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M2.5 8.5l3.5 3.5 7.5-8.5"
                fill="none"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        <div className="np-body">
          {editing ? (
            <div className="np-edit">
              <textarea
                className="np-input"
                rows={2}
                value={lineEdit?.text || ""}
                onChange={(e) =>
                  setLineEdit((p) => ({ ...p, text: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveLineEdit();
                  if (e.key === "Escape") {
                    setEditingLineId(null);
                    setLineEdit(null);
                  }
                }}
              />

              <div className="np-fieldgrid">
                <label className="np-field">
                  <span className="np-duelabel">Service type</span>
                  <select
                    className="np-select"
                    value={lineEdit?.serviceType || ""}
                    onChange={(e) =>
                      setLineEdit((p) => ({ ...p, serviceType: e.target.value }))
                    }
                  >
                    <option value="">Select service type</option>
                    {withCurrentValue(serviceTypes, lineEdit?.serviceType).map(
                      (t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="np-field">
                  <span className="np-duelabel">Due</span>
                  <input
                    type="date"
                    className="np-dateinput"
                    value={lineEdit?.dueDate || ""}
                    onChange={(e) =>
                      setLineEdit((p) => ({ ...p, dueDate: e.target.value }))
                    }
                  />
                </label>

                <div className="np-field np-field--wide">
                  <span className="np-duelabel">Tech assign</span>
                  {renderTechPicker(lineEdit?.techAssign || [""], (next) =>
                    setLineEdit((p) => ({ ...p, techAssign: next }))
                  )}
                </div>
              </div>

              <div className="np-editbar">
                <button
                  type="button"
                  className="np-btn np-btn--primary np-btn--sm"
                  onClick={saveLineEdit}
                  disabled={busy || !(lineEdit?.text || "").trim()}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="np-btn np-btn--ghost np-btn--sm"
                  onClick={() => {
                    setEditingLineId(null);
                    setLineEdit(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="np-line">
                <span
                  className="np-stamp"
                  title={`Written ${fullDate(line.createdAt)}`}
                >
                  {stampDate(line.createdAt)}
                </span>
                <span className="np-text">{highlight(line.text, terms)}</span>
                {!!line.serviceType && (
                  <span className="np-tag np-tag--svc">{line.serviceType}</span>
                )}
                {(line.techAssign || []).map((t) => (
                  <span className="np-tag" key={t}>
                    {t}
                  </span>
                ))}
                {line.updatedAt && (
                  <span className="np-tag np-tag--quiet">edited</span>
                )}
                <button
                  type="button"
                  className={`np-due ${
                    line.dueDate ? `np-due--${status || "set"}` : "np-due--empty"
                  }`}
                  onClick={() => {
                    setLineDueTarget(line);
                    setDueDraft(line.dueDate || "");
                  }}
                  title={
                    line.dueDate
                      ? `Due ${line.dueDate} — click to change`
                      : "Set a due date"
                  }
                >
                  {line.dueDate ? dueChip(line, today) : "+ Due date"}
                </button>
              </p>

              {madeWO ? (
                <p className="np-woline">
                  <span className="np-wobadge">WO {line.jobWo}</span>
                  <span className="np-wometa">
                    Work order created
                    {line.jobCreatedBy ? ` by ${line.jobCreatedBy}` : ""}
                    {line.jobCreatedAt
                      ? ` · ${stampDate(line.jobCreatedAt)}`
                      : ""}
                  </span>
                </p>
              ) : (
                <p className="np-woline">
                  <button
                    type="button"
                    className="np-btn np-btn--ghost np-btn--sm"
                    onClick={() => setWoTarget({ mode: "one", line })}
                    disabled={!line.serviceType || !(line.text || "").trim()}
                    title={
                      !line.serviceType
                        ? "Pick a service type first — it decides what kind of work order this becomes"
                        : "Create a work order from this line"
                    }
                  >
                    Make work order
                  </button>
                  {!line.serviceType && (
                    <span className="np-wometa">Needs a service type</span>
                  )}
                </p>
              )}
            </>
          )}

          {!!(line.files || []).length && (
            <div className="np-files">
              {line.files.map((f) => (
                <a
                  key={f.path || f.url}
                  className="np-file"
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" className="np-clip">
                    <path
                      d="M10.5 5.5L6 10a1.8 1.8 0 002.5 2.5l4.5-4.5a3.4 3.4 0 00-4.8-4.8L3.5 7.9a5 5 0 007 7l4-4"
                      fill="none"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {f.name}
                </a>
              ))}
            </div>
          )}
        </div>

        {!editing && (
          <div className="np-tools">
            <label className="np-tool" title="Attach a file">
              <input
                type="file"
                multiple
                onChange={(e) => handleLineFiles(line, e)}
              />
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M10.5 5.5L6 10a1.8 1.8 0 002.5 2.5l4.5-4.5a3.4 3.4 0 00-4.8-4.8L3.5 7.9a5 5 0 007 7l4-4"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </label>
            <button
              type="button"
              className="np-tool"
              title="Edit this line"
              onClick={() => startLineEdit(line)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M11.2 2.3l2.5 2.5L5.4 13 2 14l1-3.4 8.2-8.3z"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="np-tool np-tool--danger"
              title="Delete this line"
              onClick={() => setConfirmingLine({ kind: "delete", line })}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9"
                  fill="none"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  /* -------------------------------------------------- render */

  const emptyMessage = () => {
    if (loadingNotes) return "Opening the pad…";
    if (terms.length) return `No lines match “${search.trim()}”.`;
    if (tab === "completed") return "Nothing completed yet.";
    return viewingSelf
      ? "Blank pad. Hit + New line and start writing."
      : `${viewUserName} hasn't written anything yet.`;
  };

  return (
    <div className="np-page">
      <header className="np-bar">
        <div className="np-bar__row">
          <div className="np-ident">
            <h1 className="np-title">Call Pad</h1>
            <p className="np-who">
              {tab === "projects"
                ? activeProject
                  ? `Project · WO ${activeProject.woStem}`
                  : "Projects · shared"
                : viewingSelf
                ? "Your pad"
                : `${viewUserName}'s pad`}
            </p>
          </div>

          <div className="np-search">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" fill="none" strokeWidth="1.5" />
              <path d="M10.5 10.5L14 14" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder={
                tab === "projects"
                  ? activeProject
                    ? "Search these lines"
                    : "Search projects"
                  : "Search these notes"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search notes"
            />
            {!!search && (
              <button
                type="button"
                className="np-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {isAdmin && (
            <div className="np-picker">
              <label htmlFor="np-user">Pad</label>
              <select
                id="np-user"
                value={viewUserId || ""}
                onChange={(e) => setViewUserId(e.target.value)}
              >
                <option value={authUser.uid}>
                  {displayName(myProfile || { email: authUser.email })} (you)
                </option>
                {users
                  .filter((u) => u.id !== authUser.uid)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {displayName(u)}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* On the Projects tab this button follows where you are: the
              project list makes a project, an open project makes a line. */}
          {tab === "projects" ? (
            activeProject ? (
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={startLineDraft}
              >
                + New line
              </button>
            ) : (
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={openNewProject}
              >
                + New project
              </button>
            )
          ) : (
            <button
              type="button"
              className="np-btn np-btn--primary"
              onClick={startDraft}
            >
              + New line
            </button>
          )}
        </div>

        <div className="np-bar__row np-bar__row--sub">
          <div className="np-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "open"}
              className={`np-tab${tab === "open" ? " is-active" : ""}`}
              onClick={() => setTab("open")}
            >
              Pad <span className="np-count">{openNotes.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "completed"}
              className={`np-tab${tab === "completed" ? " is-active" : ""}`}
              onClick={() => setTab("completed")}
            >
              Completed <span className="np-count">{doneNotes.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "projects"}
              className={`np-tab${tab === "projects" ? " is-active" : ""}`}
              onClick={() => {
                setTab("projects");
                setDraftOpen(false);
                setEditingId(null);
              }}
            >
              Projects <span className="np-count">{projects.length}</span>
            </button>
          </div>

          <button
            type="button"
            className="np-btn np-btn--ghost np-btn--sm"
            onClick={() =>
              setSortDir((p) => (p === "oldest" ? "newest" : "oldest"))
            }
            title={
              tab === "open"
                ? "Sort by the date written"
                : "Sort by the date completed"
            }
          >
            {sortDir === "oldest" ? "Oldest first" : "Newest first"}
          </button>
        </div>

        {!!otherTabHits && (
          <p className="np-hint">
            {otherTabHits} match{otherTabHits === 1 ? "" : "es"} on the{" "}
            <button
              type="button"
              className="np-link"
              onClick={() => setTab(tab === "open" ? "completed" : "open")}
            >
              {tab === "open" ? "Completed" : "Pad"}
            </button>{" "}
            tab.
          </p>
        )}

        {!!error && <p className="np-error">{error}</p>}
        {uploading && <p className="np-hint">Uploading…</p>}
      </header>

      <div className="np-pad">
        <div className="np-binding" aria-hidden="true" />
        <div className="np-sheet">
          <div className="np-margin" aria-hidden="true" />

          {tab === "projects" ? (
            <>
              {activeProject ? (
                <>
                  <div className="np-projhead">
                    <button
                      type="button"
                      className="np-btn np-btn--ghost np-btn--sm"
                      onClick={() => {
                        setActiveProjectId(null);
                        resetLineDraft();
                        setEditingLineId(null);
                      }}
                    >
                      ← All projects
                    </button>

                    <div className="np-projhead__ident">
                      <span className="np-project__wo">
                        WO {activeProject.woStem}
                      </span>
                      <span className="np-projhead__site">
                        {activeProject.siteName}
                        {activeProject.name ? ` — ${activeProject.name}` : ""}
                      </span>
                      {!!activeProject.scope && (
                        <span className="np-project__scope">
                          {activeProject.scope}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="np-btn np-btn--ghost np-btn--sm"
                      onClick={() => setWoTarget({ mode: "all" })}
                      disabled={!convertibleLines.length}
                      title={
                        convertibleLines.length
                          ? "Create a work order for every line that doesn't have one yet"
                          : "Every line either has a work order already or is missing a service type"
                      }
                    >
                      Make all work orders ({convertibleLines.length})
                    </button>
                  </div>

                  {activeLines.length === 0 && !lineDraftOpen && (
                    <p className="np-empty">
                      {terms.length
                        ? `No lines match “${search.trim()}”.`
                        : "No lines yet. Hit + New line to add the first one."}
                    </p>
                  )}

                  {activeLines.map(renderProjectLine)}
                </>
              ) : (
                <>
                  {visibleProjects.length === 0 && (
                    <p className="np-empty">
                      {loadingProjects
                        ? "Loading projects…"
                        : terms.length
                        ? `No projects match “${search.trim()}”.`
                        : "No projects yet. Hit + New project to start one."}
                    </p>
                  )}
                  {visibleProjects.map(renderProjectCard)}
                </>
              )}
            </>
          ) : (
            <>
              {visible.length === 0 && !draftOpen && (
                <p className="np-empty">{emptyMessage()}</p>
              )}

              {visible.map(renderRow)}
            </>
          )}

          {draftOpen && tab === "open" && (
            <div className="np-row np-row--draft">
              <span className="np-check np-check--ghost" aria-hidden="true" />
              <div className="np-body">
                <textarea
                  ref={draftRef}
                  className="np-input"
                  rows={1}
                  value={draftText}
                  placeholder={
                    viewingSelf
                      ? "Write the note…"
                      : `Write a note on ${viewUserName}'s pad…`
                  }
                  onChange={(e) => setDraftText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveDraft();
                    if (e.key === "Escape") cancelDraft();
                  }}
                />

                {!!draftFiles.length && (
                  <div className="np-files">
                    {draftFiles.map((f) => (
                      <span className="np-file" key={f.path}>
                        {f.name}
                        <button
                          type="button"
                          className="np-filex"
                          aria-label={`Remove ${f.name}`}
                          onClick={() =>
                            setDraftFiles((prev) =>
                              prev.filter((x) => x.path !== f.path)
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="np-editbar">
                  <button
                    type="button"
                    className="np-btn np-btn--primary np-btn--sm"
                    onClick={saveDraft}
                    disabled={busy || (!draftText.trim() && !draftFiles.length)}
                  >
                    Save line
                  </button>
                  <label className="np-btn np-btn--ghost np-btn--sm np-attach">
                    Attach file
                    <input type="file" multiple onChange={handleDraftFiles} />
                  </label>
                  <label className="np-duefield">
                    <span className="np-duelabel">Due</span>
                    <input
                      type="date"
                      className="np-dateinput"
                      value={draftDue}
                      onChange={(e) => setDraftDue(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="np-btn np-btn--ghost np-btn--sm"
                    onClick={cancelDraft}
                  >
                    Cancel
                  </button>
                  <span className="np-kbd">⌘ + Enter saves</span>
                </div>
              </div>
            </div>
          )}

          {tab === "projects" && activeProject && lineDraftOpen && (
            <div className="np-row np-row--draft np-row--line">
              <span className="np-check np-check--ghost" aria-hidden="true" />
              <div className="np-body">
                <textarea
                  className="np-input"
                  rows={2}
                  value={lineText}
                  placeholder="What needs doing? e.g. Treat units 101–110 for roaches"
                  onChange={(e) => setLineText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                      saveLineDraft();
                    if (e.key === "Escape") resetLineDraft();
                  }}
                />

                <div className="np-fieldgrid">
                  <label className="np-field">
                    <span className="np-duelabel">Service type</span>
                    <select
                      className="np-select"
                      value={lineServiceType}
                      onChange={(e) => setLineServiceType(e.target.value)}
                    >
                      <option value="">Select service type</option>
                      {withCurrentValue(serviceTypes, lineServiceType).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="np-field">
                    <span className="np-duelabel">Due</span>
                    <input
                      type="date"
                      className="np-dateinput"
                      value={lineDue}
                      onChange={(e) => setLineDue(e.target.value)}
                    />
                  </label>

                  <div className="np-field np-field--wide">
                    <span className="np-duelabel">Tech assign</span>
                    {renderTechPicker(lineTechs, setLineTechs)}
                  </div>
                </div>

                {!!lineFiles.length && (
                  <div className="np-files">
                    {lineFiles.map((f) => (
                      <span className="np-file" key={f.path}>
                        {f.name}
                        <button
                          type="button"
                          className="np-filex"
                          aria-label={`Remove ${f.name}`}
                          onClick={() =>
                            setLineFiles((prev) =>
                              prev.filter((x) => x.path !== f.path)
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="np-editbar">
                  <button
                    type="button"
                    className="np-btn np-btn--primary np-btn--sm"
                    onClick={saveLineDraft}
                    disabled={busy || (!lineText.trim() && !lineFiles.length)}
                  >
                    Save line
                  </button>
                  <label className="np-btn np-btn--ghost np-btn--sm np-attach">
                    Attach file
                    <input type="file" multiple onChange={handleLineDraftFiles} />
                  </label>
                  <button
                    type="button"
                    className="np-btn np-btn--ghost np-btn--sm"
                    onClick={resetLineDraft}
                  >
                    Cancel
                  </button>
                  <span className="np-kbd">⌘ + Enter saves</span>
                </div>
              </div>
            </div>
          )}

          <div ref={padEndRef} />

          {tab === "open" && !draftOpen && (
            <button type="button" className="np-newline" onClick={startDraft}>
              + New line
            </button>
          )}

          {tab === "projects" && activeProject && !lineDraftOpen && (
            <button type="button" className="np-newline" onClick={startLineDraft}>
              + New line
            </button>
          )}

          {tab === "projects" && !activeProject && (
            <button type="button" className="np-newline" onClick={openNewProject}>
              + New project
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <NpModal
          onClose={() => setConfirming(null)}
        >
            <h2>
              {confirming.kind === "complete" && "Was this completed?"}
              {confirming.kind === "reopen" && "Put this back on the pad?"}
              {confirming.kind === "delete" && "Delete this line?"}
            </h2>
            <p className="np-dialog__note">{confirming.note.text}</p>
            <p className="np-dialog__sub">
              {confirming.kind === "complete" &&
                "It moves to Completed and stops showing on the pad."}
              {confirming.kind === "reopen" &&
                "It returns to the pad in date order."}
              {confirming.kind === "delete" &&
                "This removes it for good. It can't be undone."}
            </p>
            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setConfirming(null)}
              >
                {confirming.kind === "complete" ? "Not yet" : "Cancel"}
              </button>
              <button
                type="button"
                className={`np-btn ${
                  confirming.kind === "delete"
                    ? "np-btn--danger"
                    : "np-btn--primary"
                }`}
                onClick={runConfirm}
                disabled={busy}
              >
                {confirming.kind === "complete" && "Yes, completed"}
                {confirming.kind === "reopen" && "Move to pad"}
                {confirming.kind === "delete" && "Delete"}
              </button>
            </div>
        </NpModal>
      )}
      {dueTarget && (
        <NpModal
          onClose={() => setDueTarget(null)}
          labelledBy="np-duedlg-title"
        >
            <h2 id="np-duedlg-title">
              {dueTarget.dueDate ? "Change the due date" : "Set a due date"}
            </h2>
            <p className="np-dialog__note">{dueTarget.text}</p>

            <div className="np-quickdue">
              {[
                ["Today", 0],
                ["Tomorrow", 1],
                ["In 3 days", 3],
                ["Next week", 7],
              ].map(([label, days]) => {
                const value = shiftToday(days);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`np-quick${
                      dueDraft === value ? " is-active" : ""
                    }`}
                    onClick={() => setDueDraft(value)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="np-duefield np-duefield--dialog">
              <label className="np-duelabel" htmlFor="np-duedlg-input">
                Or pick a date
              </label>
              <input
                id="np-duedlg-input"
                ref={dueInputRef}
                type="date"
                className="np-dateinput"
                value={dueDraft}
                onChange={(e) => setDueDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && dueDraft) saveDue(dueDraft);
                }}
              />
            </div>

            <p className="np-dialog__sub">
              {dueDraft
                ? `${dueLong(dueDraft)} — the line turns yellow ${DUE_SOON_DAYS} days out, then orange once it's late. Completing it clears the colour.`
                : "No due date set. The line stays plain on the pad."}
            </p>

            <div className="np-dialog__actions">
              {!!dueTarget.dueDate && (
                <button
                  type="button"
                  className="np-btn np-btn--ghost np-dialog__clear"
                  onClick={() => saveDue("")}
                  disabled={busy}
                >
                  Clear date
                </button>
              )}
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setDueTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={() => saveDue(dueDraft)}
                disabled={busy || !dueDraft}
              >
                Save due date
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- new project */}
      {newProjectOpen && (
        <NpModal
          onClose={() => closeNewProject()}
          wide
        >
            <h2>New project</h2>
            <p className="np-dialog__sub">
              Pick the site, then the work order this project belongs to. Every
              line you add here can become its own work order branched off that
              number.
            </p>

            {pickerLoading && <p className="np-hint">Loading sites and work orders…</p>}

            <label className="np-field">
              <span className="np-duelabel">Site name</span>
              <select
                className="np-select"
                value={npSite}
                onChange={(e) => {
                  setNpSite(e.target.value);
                  // The WO list is per-site, so the old pick can't survive
                  // a site change without pointing at the wrong property.
                  setNpWoStem("");
                }}
              >
                <option value="">Select a site</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.propertyName}>
                    {p.propertyName}
                  </option>
                ))}
              </select>
            </label>

            <label className="np-field">
              <span className="np-duelabel">WO — scope</span>
              <select
                className="np-select"
                value={npWoStem}
                onChange={(e) => setNpWoStem(e.target.value)}
                disabled={!npSite}
              >
                <option value="">
                  {npSite ? "Select a work order" : "Pick a site first"}
                </option>
                {npWoOptions.map((o) => (
                  <option key={o.root} value={o.root}>
                    {o.root}
                    {o.scope ? ` — ${o.scope.slice(0, 70)}` : " — No scope"}
                  </option>
                ))}
              </select>
            </label>

            {npSite && !pickerLoading && npWoOptions.length === 0 && (
              <p className="np-hint">
                No work orders found for {npSite}. Schedule a job for this site
                first, then the project can hang off it.
              </p>
            )}

            <label className="np-field">
              <span className="np-duelabel">Project name (optional)</span>
              <input
                type="text"
                className="np-textinput"
                value={npName}
                placeholder="e.g. Building A quarterly treatments"
                onChange={(e) => setNpName(e.target.value)}
              />
            </label>

            {!!npChosenWo && (
              <p className="np-dialog__sub">
                New lines turned into work orders will be numbered{" "}
                <strong>{npWoStem}-2</strong>, <strong>{npWoStem}-3</strong> and
                so on, so everything stays grouped under {npWoStem}.
              </p>
            )}

            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={closeNewProject}
              >
                Cancel
              </button>
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={saveNewProject}
                disabled={busy || !npSite || !npWoStem}
              >
                Create project
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- project line due date */}
      {lineDueTarget && (
        <NpModal
          onClose={() => setLineDueTarget(null)}
        >
            <h2>{lineDueTarget.dueDate ? "Change the due date" : "Set a due date"}</h2>
            <p className="np-dialog__note">{lineDueTarget.text}</p>

            <div className="np-quickdue">
              {[
                ["Today", 0],
                ["Tomorrow", 1],
                ["In 3 days", 3],
                ["Next week", 7],
              ].map(([label, days]) => {
                const value = shiftToday(days);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`np-quick${dueDraft === value ? " is-active" : ""}`}
                    onClick={() => setDueDraft(value)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="np-duefield np-duefield--dialog">
              <span className="np-duelabel">Or pick a date</span>
              <input
                type="date"
                className="np-dateinput"
                value={dueDraft}
                onChange={(e) => setDueDraft(e.target.value)}
              />
            </div>

            <p className="np-dialog__sub">
              {dueDraft
                ? `${dueLong(dueDraft)} — this becomes the start date on the work order made from this line.`
                : "No due date set. The work order will be created with no start date."}
            </p>

            <div className="np-dialog__actions">
              {!!lineDueTarget.dueDate && (
                <button
                  type="button"
                  className="np-btn np-btn--ghost np-dialog__clear"
                  onClick={() => saveLineDue("")}
                  disabled={busy}
                >
                  Clear date
                </button>
              )}
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setLineDueTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={() => saveLineDue(dueDraft)}
                disabled={busy || !dueDraft}
              >
                Save due date
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- project line confirm */}
      {confirmingLine && (
        <NpModal
          onClose={() => setConfirmingLine(null)}
        >
            <h2>
              {confirmingLine.kind === "complete" && "Was this completed?"}
              {confirmingLine.kind === "reopen" && "Put this back on the project?"}
              {confirmingLine.kind === "delete" && "Delete this line?"}
            </h2>
            <p className="np-dialog__note">{confirmingLine.line.text}</p>
            <p className="np-dialog__sub">
              {confirmingLine.kind === "complete" &&
                "It stays on the project, marked done."}
              {confirmingLine.kind === "reopen" && "It goes back to open."}
              {confirmingLine.kind === "delete" &&
                (confirmingLine.line.jobId
                  ? `This removes the line for good. Work order ${confirmingLine.line.jobWo} stays scheduled — delete that from Scheduling if you don't want it.`
                  : "This removes it for good. It can't be undone.")}
            </p>
            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setConfirmingLine(null)}
              >
                {confirmingLine.kind === "complete" ? "Not yet" : "Cancel"}
              </button>
              <button
                type="button"
                className={`np-btn ${
                  confirmingLine.kind === "delete"
                    ? "np-btn--danger"
                    : "np-btn--primary"
                }`}
                onClick={runLineConfirm}
                disabled={busy}
              >
                {confirmingLine.kind === "complete" && "Yes, completed"}
                {confirmingLine.kind === "reopen" && "Reopen"}
                {confirmingLine.kind === "delete" && "Delete"}
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- delete project */}
      {confirmingProject && (
        <NpModal
          onClose={() => setConfirmingProject(null)}
        >
            <h2>Delete this project?</h2>
            <p className="np-dialog__note">
              WO {confirmingProject.woStem} — {confirmingProject.siteName}
            </p>
            <p className="np-dialog__sub">
              This deletes the project and all{" "}
              {(linesByProject.get(confirmingProject.id) || []).length} of its
              lines. Any work orders already created stay scheduled — they're
              real jobs now and this won't touch them.
            </p>
            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setConfirmingProject(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="np-btn np-btn--danger"
                onClick={deleteProject}
                disabled={busy}
              >
                Delete project
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- make work orders */}
      {woTarget && activeProject && (
        <NpModal
          onClose={() => {
            if (!woBusy) setWoTarget(null);
          }}
          wide
        >
            <h2>
              {woTarget.mode === "all"
                ? `Make ${convertibleLines.length} work order${
                    convertibleLines.length === 1 ? "" : "s"
                  }?`
                : "Make a work order?"}
            </h2>

            <p className="np-dialog__sub">
              {woTarget.mode === "all"
                ? `Each line below becomes its own job branched off WO ${activeProject.woStem}.`
                : `This becomes a job branched off WO ${activeProject.woStem}.`}
            </p>

            <div className="np-wopreview">
              {(woTarget.mode === "all"
                ? convertibleLines
                : [woTarget.line].filter(Boolean)
              ).map((l) => (
                <div className="np-wopreview__row" key={l.id}>
                  <span className="np-tag np-tag--svc">
                    {l.serviceType || "No service type"}
                  </span>
                  <span className="np-wopreview__text">{l.text}</span>
                  <span className="np-wopreview__meta">
                    Start {l.dueDate || "—"} ·{" "}
                    {(l.techAssign || []).join(", ") || "No tech"} ·{" "}
                    {(l.files || []).length} file
                    {(l.files || []).length === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>

            <p className="np-dialog__sub">
              The due date becomes the job's start date, the techs carry over,
              and any files attached here come across with it. Numbers are
              assigned when you confirm, so nothing collides with a work order
              someone else made in the meantime.
            </p>

            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--ghost"
                onClick={() => setWoTarget(null)}
                disabled={woBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={makeWorkOrders}
                disabled={woBusy}
              >
                {woBusy ? "Creating…" : "Create work orders"}
              </button>
            </div>
        </NpModal>
      )}

      {/* ---------------------------------------------- work order result */}
      {!!woResult && (
        <NpModal
          onClose={() => setWoResult("")}
        >
            <h2>Done</h2>
            <p className="np-dialog__sub">{woResult}</p>
            <div className="np-dialog__actions">
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={() => setWoResult("")}
              >
                Close
              </button>
            </div>
        </NpModal>
      )}
    </div>
  );
};

export default Notebook;
